import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { CURRENT_TERMS_VERSION } from "@/api/terms";

// Keep this page in sync with the recorded terms version: bump
// CURRENT_TERMS_VERSION (src/api/terms.ts) and current_terms_version() (SQL)
// whenever this text changes materially, so prior acceptances stay traceable.
// Quizrun runs across multiple jurisdictions (UK, Czechia, Germany, Cyprus, US)
// where liability rules differ — several EU jurisdictions restrict waiving
// liability for personal injury, so the waiver below is written to yield to
// local law rather than override it.

const Terms = () => {
  useEffect(() => {
    document.title = "Terms & Liability Waiver — Quizrun";
  }, []);

  return (
    <main>
      <PageHeader
        eyebrow="Legal"
        title="Terms & Conditions and Liability Waiver"
        intro={
          <>
            Quizrun is a live event that combines running with solving
            lateral-thinking puzzles, held in outdoor public spaces such as
            parks. No running experience needed — many people walk it, and you
            can slow down, rest or stop at any time. Please read these terms and
            the liability waiver carefully before registering or taking part.
          </>
        }
      />

      <section className="container mx-auto px-4 md:px-8 pb-16">
        <div className="max-w-2xl space-y-6 text-sm leading-relaxed text-muted-foreground">
          <div className="text-xs text-muted-foreground">
            Version {CURRENT_TERMS_VERSION}
          </div>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">1. Participation</h2>
            <p>
              By registering for a Quizrun event you agree to
              follow event rules, the instructions of organisers and marshals,
              and all applicable local laws while taking part. Quizrun involves
              both physical activity (running or moving between points) and a
              stationary problem-solving element. Events vary in distance,
              terrain and difficulty — from short runs in parks to longer trail
              events with significant elevation and uneven or steep ground. It is
              your responsibility to check the details of the specific event you
              register for and to satisfy yourself that you are able to complete
              it safely.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              2. Assumption of risk
            </h2>
            <p>
              Running and physical activity carry inherent risks,
              including but not limited to trips, falls, collisions, uneven,
              slippery or steep ground, trail and off-road terrain, changes in
              elevation, natural obstacles, exposure and weather, other
              participants and members of the public, traffic and pre-existing
              medical conditions. Longer, trail or timed events carry greater
              risk, and the competitive nature of a timed event may encourage you
              to push yourself — you remain responsible for running within your
              own limits. You understand and voluntarily accept these risks by
              choosing to take part.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              3. Your health &amp; safety
            </h2>
            <p>
              By registering, you confirm that you are medically fit to take
              part and are not aware of any medical condition that makes
              participation unsafe for you, and that you are fit enough to
              complete the specific event you have registered for, including its
              distance and terrain. You take part of your own choice, having
              considered your own health. Please don&apos;t take part if you have
              been advised not to exercise, and stop and tell an organiser if you
              feel unwell &mdash; safety first. In an emergency, organisers may
              arrange first aid or call emergency services on your behalf.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              4. Liability waiver
            </h2>
            <p>
              To the fullest extent permitted by applicable law,
              you agree that the organisers are not liable for loss, injury or
              damage arising from your participation, except where such
              liability cannot be excluded by law. Nothing in these terms
              excludes or limits liability for death or personal injury caused
              by negligence, fraud, or any other liability that cannot be
              excluded or limited under the law that applies to your event.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              5. Age &amp; minors
            </h2>
            <p>
              Participants under the age of 18 may take part only
              with the consent and, where required, the accompaniment of a
              parent or legal guardian, who accepts these terms and the
              liability waiver on the minor&apos;s behalf. The organisers may set
              a minimum age for specific events.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              6. Photography &amp; media
            </h2>
            <p>
              Photographs and video may be taken at Quizrun events and used by
              the organisers to promote Quizrun, including on the website and
              social media. We rely on our legitimate interest in promoting the
              events to do this. If you do not wish to appear in event media,
              please tell an organiser in advance or email us, and we will make
              reasonable efforts to accommodate your request.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              7. Team registrations
            </h2>
            <p>
              If you register a team or add other participants, you confirm you
              have their permission to do so and are responsible for making them
              aware of these terms and the liability waiver. By taking part,
              each participant accepts these terms for themselves; organisers
              may also collect confirmation at the event. For any participant
              under 18, their parent or guardian accepts these terms on their
              behalf.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              8. Tickets, cancellation &amp; transfers
            </h2>
            <p>
              Some events require a paid ticket. Tickets are{" "}
              <span className="font-medium text-foreground">
                non-refundable
              </span>
              . If you are unable to attend an event you have paid for, your
              payment can be transferred to a future Quizrun event instead of
              refunded — contact us to arrange the transfer. If an event is
              cancelled by the organisers, we will offer to transfer your ticket
              to another event. This does not affect any rights you may have
              under mandatory consumer law that cannot be excluded.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">9. Data &amp; privacy</h2>
            <p>
              When you accept these terms we record who accepted, the date and
              time, the version of the terms, and your health self-certification,
              so we can demonstrate what was agreed. For details of what personal
              data we collect and how we use it, see our{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              10. Governing law
            </h2>
            <p>
              Quizrun events run in several countries. Unless local law that
              applies to you provides otherwise, these terms and any dispute
              relating to them are governed by the law of the country where the
              event you registered for takes place, and you accept that
              country&apos;s courts may hear any dispute. Nothing here removes
              protections that the law of your home country gives you and that
              cannot be excluded by agreement.
            </p>
          </section>

          <p className="text-xs">
            Questions? Email{" "}
            <a
              href="mailto:quizrun.org@gmail.com"
              className="underline underline-offset-2 hover:text-foreground"
            >
              quizrun.org@gmail.com
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
};

export default Terms;
